import { STANDARD_THEME_KEY, type ThemeAvailability } from "./theme";

/*
 * The catalog of themes (ADR 0050 section 2): every theme HoW offers, with the two facts the database needs to refuse
 * a theme a church may not use. `pnpm catalog:generate` writes these rows into `public.platform_themes`, the same way
 * the module registry becomes `platform_modules`, and `pnpm catalog:check` fails when this list and the newest catalog
 * migration disagree.
 *
 * A theme package (`packages/theme-<key>`) carries the same key, name and availability in its manifest; the public
 * site's conformance test (`apps/public-web/src/lib/themes.test.ts`) fails when a registered package and its entry here
 * drift apart. The list is plain data so it can be read without loading any theme's renderers.
 *
 * Withdrawing a theme is setting its status to `withdrawn` and regenerating: churches on it fall back to `standard`
 * at their next page view (`private.site_brand`), with their content untouched. A key is never reused.
 */

export const THEME_STATUSES = ["active", "withdrawn"] as const;
export type ThemeStatus = (typeof THEME_STATUSES)[number];

export interface ThemeCatalogEntry {
  /** Stable and lower-case (`THEME_KEY_PATTERN`); what a church stores in `tenant_settings.branding.theme`. */
  readonly key: string;
  readonly name: string;
  readonly availability: ThemeAvailability;
  readonly status: ThemeStatus;
  /** The order the Look page lists themes in. */
  readonly sortOrder: number;
}

export const THEME_CATALOG: readonly ThemeCatalogEntry[] = [
  {
    key: STANDARD_THEME_KEY,
    name: "Standard",
    availability: "included",
    status: "active",
    sortOrder: 1,
  },
  // The second included theme (ADR 0050, the owner's answer 2): large serif headlines, full-bleed photography, more
  // white space. Its package is `packages/theme-editorial`.
  { key: "editorial", name: "Editorial", availability: "included", status: "active", sortOrder: 2 },
  // The third included theme (ADR 0073): a product page rather than a magazine. Centred headlines, photographs in
  // large rounded frames, bands of white and soft grey, pill buttons and a two-row header. Its package is
  // `packages/theme-revival`.
  { key: "revival", name: "Revival", availability: "included", status: "active", sortOrder: 3 },
];

/** The words a church sees beside a theme's name on the Look page. */
export const THEME_AVAILABILITY_LABELS: Record<ThemeAvailability, string> = {
  included: "Included",
  premium: "Premium",
  bespoke: "Made for your church",
};
